import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { IDashboardDateRange, IDashboardKpi } from "../../../@types/admin";
import { IAdminRouteItem } from "../../../@types/AdminRoute";
import { SkeletonLoading } from "../../../components/Skeleton";
import { useDashboardKpis } from "../../../hooks/queries/admin/dashboard";
import { getVisibleAdminMenuGroups } from "../../../routes/admin.routes";
import { isForbidden } from "../../../utils/HttpError";
import {
  Container,
  CounterCard,
  CounterDelta,
  CounterGrid,
  CounterLabel,
  CounterRule,
  CounterValue,
  NavGrid,
  NavGroupCard,
  NavGroupTitle,
  NavLink,
  NavList,
  NoticeCard,
  Section,
  SectionHeading,
  SectionNote,
  SectionTitle,
} from "./AdminOverview.styles";

/** The counters' window, in hours. */
const WINDOW_HOURS = 24;

/**
 * The three counters worth showing over a day, and — the point of the panel —
 * exactly how each is counted.
 *
 * Every rule here was read off the SQL in `AdminDashboardCmsRepository` and
 * `AdminDashboardRepository`, not inferred from the label. Two of them are
 * genuinely surprising:
 *
 * - "Active" is a UNION of three unrelated activities, so a patient who only
 *   opened the app counts as active without recording anything.
 * - The counts are per PATIENT-WARD PAIR, not per patient: the query joins
 *   `PatientWards` so a patient in three wards contributes three. That is
 *   deliberate — it makes the total equal the sum of the per-ward breakdown on
 *   the reports dashboard — but it means the figure is not "how many people".
 *
 * `avgWardDays` is deliberately NOT shown. Its SQL measures
 * `LEAST(end, To) - GREATEST(created, From)`, so over a 24-hour window every
 * patient who existed before the window contributes exactly one day and the
 * average is always ≈1. It is a meaningful figure over a month and a
 * meaningless one here.
 */
const COUNTERS: {
  key: "activePatients" | "createdPatients" | "workflowsTriggered";
  label: string;
  rule: string;
}[] = [
  {
    key: "activePatients",
    label: "Active patients",
    rule:
      "A patient is active if, in the last 24 hours, they recorded a " +
      "measurement, answered a survey question, or last used the app. Any " +
      "one of the three is enough. Each patient is counted once, however " +
      "many wards they belong to — the per-ward chart below still counts " +
      "them in every ward, so its bars can add up to more than this.",
  },
  {
    key: "createdPatients",
    label: "New patients",
    rule:
      "Patient records created in the last 24 hours, each counted once. " +
      "Only published wards count, so a patient whose wards are all " +
      "unpublished is invisible here.",
  },
  {
    key: "workflowsTriggered",
    label: "Workflows started",
    rule:
      "Workflow runs that began for a patient in the last 24 hours. A " +
      "patient entering the same workflow twice counts twice; a run " +
      "advancing through its steps does not count again.",
  },
];

function formatDelta(kpi: IDashboardKpi): {
  direction: "up" | "down" | "flat";
  text: string;
} {
  // deltaPct is null when the previous window was 0 and this one isn't — a
  // percentage of nothing. Say so rather than printing a misleading 0%.
  if (kpi.deltaPct === null || kpi.deltaPct === undefined) {
    return { direction: "flat", text: "no comparable previous 24 hours" };
  }

  const magnitude = `${Math.abs(kpi.deltaPct).toFixed(1)}%`;
  if (kpi.deltaPct > 0) {
    return { direction: "up", text: `▲ ${magnitude} vs the previous 24 hours` };
  }
  if (kpi.deltaPct < 0) {
    return {
      direction: "down",
      text: `▼ ${magnitude} vs the previous 24 hours`,
    };
  }
  return { direction: "flat", text: "→ unchanged vs the previous 24 hours" };
}

/**
 * Landing page for the /admin section (EN-5176), replacing Retool's Home page
 * — which showed a heading, "Hello {{ current_user.firstName }}", and a query
 * with no SQL in it. The greeting itself, and the explanatory line that used
 * to sit under it, are gone (EN-5146 / EN-5222 UI feedback): the page opens
 * straight on the counters.
 *
 * Three things a person arriving here actually wants:
 *
 * 1. Whether the platform is doing anything right now — three activity
 *    counters over the last 24 hours, each printed with its counting rule.
 *    These figures are easy to misread (see COUNTERS), so the rule is on the
 *    card, not in a tooltip.
 * 2. Where to go — every page they can reach, grouped as the side menu groups
 *    them, filtered by the same per-page permissions (EN-5129).
 * 3. Which environment they are on — already on the fixed banner
 *    AdminLayout renders at the bottom of every /admin page, so it is not
 *    repeated here.
 */
export function AdminOverview() {
  document.title = "Admin - patientMpower";

  const navigate = useNavigate();

  // Computed ONCE per mount. The KPI query key is built from the range's ISO
  // strings, so a range rebuilt each render would change the key every render
  // and refetch forever.
  const range: IDashboardDateRange = useMemo(() => {
    const endDate = new Date();
    const startDate = new Date(
      endDate.getTime() - WINDOW_HOURS * 60 * 60 * 1000
    );
    return { startDate, endDate };
  }, []);

  // An empty ward selection means "every ward", platform-wide: the API
  // resolves it from GetAllWardsAsync, which returns every published ward for
  // an internal @patientmpower.com account. This is a super-admin landing
  // page, so the figure is not narrowed to whichever wards the operator
  // happens to be rostered on as staff.
  const { data: kpis, isLoading, isError, error } = useDashboardKpis([], range);

  // The counters come from the reports endpoint, which needs
  // admin-reports:read. Plenty of /admin users hold page permissions without
  // it, and for them the panel is absent — not broken.
  const isCountersForbidden = isForbidden(error);

  const groups = getVisibleAdminMenuGroups().filter(
    (group) => group.children && group.children.length > 0
  );

  const handleNavigate = (item: IAdminRouteItem) => {
    navigate(item.path);
  };

  return (
    <Container>
      <Section>
        <SectionHeading>
          <SectionTitle>Activity in the last 24 hours</SectionTitle>
          {!isError ? (
            <SectionNote>
              Counted across every published ward on the platform, over the 24
              hours ending when this page loaded — reload for a fresh window.
              The comparison is against the 24 hours before that.
            </SectionNote>
          ) : null}
        </SectionHeading>

        {isCountersForbidden ? (
          <NoticeCard>
            These counters come from the reports data, which your account
            doesn&apos;t have access to. Ask an administrator for the
            admin-reports:read permission if you need them — everything else on
            this page works without it.
          </NoticeCard>
        ) : null}

        {isError && !isCountersForbidden ? (
          <NoticeCard>
            Couldn&apos;t load the activity counters. The rest of this page is
            unaffected.
          </NoticeCard>
        ) : null}

        {!isError ? (
          <CounterGrid>
            {COUNTERS.map((counter) => {
              const kpi = kpis?.[counter.key];
              const delta = kpi ? formatDelta(kpi) : undefined;

              return (
                <CounterCard key={counter.key}>
                  <CounterLabel>{counter.label}</CounterLabel>
                  {isLoading || !kpi ? (
                    <SkeletonLoading width={110} height={30} />
                  ) : (
                    <CounterValue>
                      {(kpi.value ?? 0).toLocaleString()}
                    </CounterValue>
                  )}
                  {delta ? (
                    <CounterDelta direction={delta.direction}>
                      {delta.text}
                    </CounterDelta>
                  ) : null}
                  <CounterRule>{counter.rule}</CounterRule>
                </CounterCard>
              );
            })}
          </CounterGrid>
        ) : null}
      </Section>

      <Section>
        <SectionHeading>
          <SectionTitle>Where to go</SectionTitle>
          <SectionNote>
            Only the pages your account can open are listed, the same filter the
            side menu uses.
          </SectionNote>
        </SectionHeading>

        <NavGrid>
          {groups.map((group) => (
            <NavGroupCard key={group.name}>
              <NavGroupTitle>{group.name}</NavGroupTitle>
              <NavList>
                {group.children?.map((item) => (
                  <li key={item.path}>
                    <NavLink
                      href={item.path}
                      onClick={(event) => {
                        // A real href keeps middle-click and copy-link
                        // working; the handler keeps the SPA navigation.
                        event.preventDefault();
                        handleNavigate(item);
                      }}
                    >
                      {item.name}
                    </NavLink>
                  </li>
                ))}
              </NavList>
            </NavGroupCard>
          ))}
        </NavGrid>
      </Section>
    </Container>
  );
}
